import React, { useContext, useEffect, useRef, useState } from 'react';
import { LanguageContext } from '../LanguageContext';

const CraftAndCodeScene = React.lazy(() => import('./CraftAndCodeScene'));

interface CraftAndCodeOptimizedProps {
    openChat?: (source?: string) => void;
}

const CraftAndCodeOptimized: React.FC<CraftAndCodeOptimizedProps> = ({ openChat }) => {
    const { t } = useContext(LanguageContext);
    const unit = t?.data?.units?.find((item: any) => item.id === 'craftcode');
    const content = unit?.pageContent;
    const services: string[] = content?.whatWeDeliver?.items || [];
    const mousePosition = useRef({ x: 0, y: 0 });
    const [sceneReady, setSceneReady] = useState(false);

    useEffect(() => {
        const isLightweightDevice = window.matchMedia('(pointer: coarse), (prefers-reduced-motion: reduce)').matches;
        if (isLightweightDevice) return;

        const enableScene = () => setSceneReady(true);
        const trackPointer = (event: PointerEvent) => {
            mousePosition.current = { x: event.clientX, y: event.clientY };
            enableScene();
        };

        window.addEventListener('pointermove', trackPointer, { once: true, passive: true });
        window.addEventListener('scroll', enableScene, { once: true, passive: true });
        return () => {
            window.removeEventListener('pointermove', trackPointer);
            window.removeEventListener('scroll', enableScene);
        };
    }, []);

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#030303] text-white selection:bg-cyan-300 selection:text-black">
            <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
                <div className="absolute inset-0 animate-pulse bg-[radial-gradient(circle_at_48%_32%,rgba(0,243,255,0.19),transparent_34%),radial-gradient(circle_at_72%_72%,rgba(188,19,254,0.16),transparent_32%),linear-gradient(#030303,#050505)] motion-reduce:animate-none" />
                <div className="absolute inset-0 opacity-25 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" />
                {sceneReady && (
                    <React.Suspense fallback={null}>
                        <CraftAndCodeScene mousePosition={mousePosition} forceVortex={false} animPhase="vortex" />
                    </React.Suspense>
                )}
            </div>

            <section className="relative z-10 flex min-h-[calc(100vh-5rem)] items-center px-5 py-24 sm:px-8 lg:px-12">
                <div className="mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-[1.25fr_.75fr]">
                    <div>
                        <p className="mb-7 font-mono text-xs font-bold uppercase tracking-[0.34em] text-cyan-300">
                            Buildhub GLOBAL / Craft &amp; Code
                        </p>
                        <h1 className="max-w-5xl text-5xl font-black leading-[0.92] tracking-[-0.055em] text-white sm:text-7xl lg:text-8xl">
                            {content?.title || 'Code that builds reality.'}
                        </h1>
                        <p className="mt-8 max-w-3xl text-lg leading-8 text-slate-300 sm:text-xl">
                            {content?.paragraph || unit?.description || ''}
                        </p>
                        <button
                            type="button"
                            onClick={() => openChat?.('unit_craftcode_hero')}
                            className="mt-10 inline-flex min-h-14 items-center justify-center border border-cyan-300 bg-cyan-300 px-8 font-mono text-sm font-black uppercase tracking-[0.14em] text-black transition hover:-translate-y-1 hover:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:ring-offset-4 focus:ring-offset-black"
                        >
                            {t?.unitPage?.ctaButton || t?.assistant?.title || 'Start a conversation'}
                        </button>
                    </div>

                    <div className="relative mx-auto aspect-square w-full max-w-sm rounded-[2rem] border border-cyan-300/25 bg-black/45 p-7 backdrop-blur-sm">
                        <div className="flex h-full flex-col justify-between rounded-[1.4rem] border border-white/10 bg-white/[0.03] p-7">
                            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                <span>System / Online</span>
                                <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300 shadow-[0_0_18px_#00f3ff] motion-reduce:animate-none" />
                            </div>
                            <div>
                                <div className="text-6xl font-black tracking-[-0.08em] text-cyan-300">C&amp;C</div>
                                <div className="mt-3 font-mono text-sm uppercase tracking-[0.18em] text-slate-400">
                                    Logic × imagination
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2" aria-hidden="true">
                                {[36, 72, 52].map((height, index) => (
                                    <div key={height} className="flex h-20 items-end rounded bg-white/[0.03] p-1">
                                        <div className="w-full bg-gradient-to-t from-purple-600 to-cyan-300" style={{ height: `${height}%`, opacity: 0.65 + index * 0.12 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {services.length > 0 && (
                <section className="relative z-10 border-t border-white/10 bg-black/65 px-5 py-24 backdrop-blur-md sm:px-8 lg:px-12">
                    <div className="mx-auto max-w-7xl">
                        <h2 className="text-3xl font-black tracking-tight sm:text-5xl">
                            {content?.whatWeDeliver?.title}
                        </h2>
                        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {services.map((service, index) => (
                                <article key={service} className="group min-h-48 border border-white/10 bg-white/[0.035] p-6 transition hover:-translate-y-1 hover:border-cyan-300/60 hover:bg-cyan-300/[0.04]">
                                    <span className="font-mono text-xs font-black text-cyan-300">{String(index + 1).padStart(2, '0')}</span>
                                    <h3 className="mt-12 text-xl font-bold leading-7 text-white">{service}</h3>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default CraftAndCodeOptimized;
